import { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Printer, X, Trophy, Check, FileDown, Usb } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { printUsb, usbSupported } from "@/lib/usbPrinter";
import Logo from "@/components/Logo";
import { PrintJobStatus } from "@/components/PrintJobStatus";

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }).map((_, i) => ({
        id: `confetti-${i}`,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        dur: 2.5 + Math.random() * 1.8,
        color: ["#F59E0B", "#059669", "#3B82F6", "#E11D48", "#FEF08A"][i % 5],
      })),
    []
  );
  return (
    <>
      {pieces.map((p) => (
        <span
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDuration: `${p.dur}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </>
  );
}

const WA_LABEL = { sent: "Inviata", sending: "Invio…" };
const emailLabel = (sent, sending) => {
  if (sent) return "Inviata";
  if (sending) return "Invio…";
  return "Email";
};
const THERMAL_IDLE = { usb: "Stampa termica USB", network_pdf: "Stampa su stampante ufficio", network: "Stampa termica di rete" };
const THERMAL_STATE = { printing: "Stampa in corso…", done: "Stampata · Ristampa termica", error: "Riprova stampa termica" };
const thermalLabel = (state, mode) => THERMAL_STATE[state] || THERMAL_IDLE[mode] || THERMAL_IDLE.network;

const CARD_INITIAL = { opacity: 0, scale: 0.9, y: 20 };
const CARD_ANIMATE = { opacity: 1, scale: 1, y: 0 };
const BADGE_INITIAL = { scale: 0, rotate: -30 };
const BADGE_ANIMATE = { scale: 1, rotate: 0 };
const BADGE_TRANSITION = { type: "spring", stiffness: 260, damping: 12, delay: 0.1 };
export default function WinReceipt({ play, contest, onClose, playSound, web = false }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [phone, setPhone] = useState("+39 ");
  const [waState, setWaState] = useState("idle"); // idle | sending | sent
  const waEnabled = !!contest.whatsapp_number;
  const [consent, setConsent] = useState(false);
  const toggleConsent = async (v) => {
    setConsent(v);
    try {
      await api.setPlayConsent(play.id, v, play.receipt_token);
    } catch (err) {
      console.error("Salvataggio consenso non riuscito:", err);
    }
  };

  const sendWhatsApp = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 8) return toast.error("Inserisci il numero WhatsApp completo");
    setWaState("sending");
    try {
      await api.sendReceiptWhatsApp(play.id, phone, play.receipt_token);
      setWaState("sent");
      toast.success("Ricevuta inviata su WhatsApp");
    } catch (e) {
      setWaState("idle");
      toast.error(e?.response?.data?.detail || "Invio WhatsApp non riuscito");
    }
  };

  const receiptText = `🎉 RICEVUTA PREMIO\nConcorso: ${contest.name}\nPremio: ${play.prize_name}${
    play.prize_value ? ` (${play.prize_value})` : ""
  }\nSeriale: ${play.receipt_serial}\nPresenta questa ricevuta al punto vendita per ritirare il premio.`;

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(receiptText)}`;

  const sendEmail = async () => {
    if (!email) return toast.error("Inserisci un indirizzo email");
    setSending(true);
    try {
      await api.sendReceiptEmail(play.id, email, play.receipt_token);
      setSent(true);
      toast.success("Ricevuta inviata via email con PDF allegato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio email non riuscito");
    } finally {
      setSending(false);
    }
  };

  const print = () => window.print();
  const [printState, setPrintState] = useState("idle"); // idle | printing | done | error
  const [queued, setQueued] = useState(false);
  const [autoPrintState, setAutoPrintState] = useState(null); // null | off | sent
  const autoPrinted = useRef(false);
  const mode = contest.printer_mode || "browser";

  const thermalPrint = async ({ manual = false } = {}) => {
    setPrintState("printing");
    try {
      if (mode === "network" || mode === "network_pdf") {
        const r = await api.printThermal(play.id, play.receipt_token);
        setPrintState("done");
        if (r.status === "queued") setQueued(true);
        else toast.success("Ricevuta stampata");
        return;
      }
      if (mode === "usb") await printUsb(play.id, { requestIfNeeded: manual, token: play.receipt_token });
      setPrintState("done");
      toast.success("Ricevuta stampata");
    } catch (e) {
      setPrintState("error");
      const msg = e?.response?.data?.detail || e?.message || "Stampa non riuscita";
      if (manual) toast.error(msg);
      else toast.warning(`${msg}. Usa "Stampa Totem" per la stampa browser.`);
    }
  };

  useEffect(() => {
    if (autoPrinted.current || web) return;
    if (mode === "browser") {
      if (!contest.auto_print) { setAutoPrintState("off"); return; }
      const t = setTimeout(() => { autoPrinted.current = true; window.print(); setAutoPrintState("sent"); }, 900);
      return () => clearTimeout(t);
    }
    autoPrinted.current = true;
    thermalPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Confetti />
      <motion.div
        initial={CARD_INITIAL}
        animate={CARD_ANIMATE}
        className="rounded-3xl bg-white text-slate-900 shadow-2xl overflow-hidden win-glow"
        data-testid="win-screen"
      >
        <div className="bg-emerald-600 text-white text-center py-8 relative">
          <button
            data-testid="close-win-button"
            onClick={onClose}
            className="absolute right-4 top-4 text-white/70 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <motion.div
            initial={BADGE_INITIAL}
            animate={BADGE_ANIMATE}
            transition={BADGE_TRANSITION}
            className="inline-grid place-items-center h-20 w-20 rounded-full bg-white/20 mb-3"
          >
            <Trophy className="h-10 w-10 text-yellow-200" />
          </motion.div>
          <h2 className="font-display font-black text-4xl tracking-tight">{contest.game?.win_title || "HAI VINTO!"}</h2>
          {contest.game?.win_message && !play.digital && <p className="text-white/85 mt-2 text-sm px-6">{contest.game.win_message}</p>}
          {play.digital && <p className="text-white/85 mt-2 text-sm px-6">Premio digitale: il tuo codice è qui sotto e nell'email, nessun ritiro in negozio.</p>}
        </div>

        {/* Receipt */}
        <div id="print-receipt" className={`p-6 sm:p-8 ${mode === "usb" || mode === "network" ? "print-thermal" : ""}`}>
          <div className="bg-amber-50 text-slate-900 font-mono-code p-6 rounded-xl border-t-4 border-b-4 border-dashed border-slate-300">
            <div className="flex justify-center mb-2"><Logo className="h-10" /></div>
            <p className="text-center text-xs uppercase tracking-widest text-slate-500">Ricevuta Premio</p>
            <h3 className="text-center font-display font-black text-2xl mt-2 mb-4">{play.prize_name}</h3>
            {play.prize_value && (
              <p className="text-center text-lg font-bold text-emerald-700 mb-4">{play.prize_value}</p>
            )}
            {play.digital ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center mb-4" data-testid="digital-prize-box">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Premio digitale · nessun ritiro in negozio</p>
                {play.digital_code ? <p className="font-mono-code text-2xl font-black tracking-[0.2em] text-emerald-800 mt-2" data-testid="digital-prize-code">{play.digital_code}</p> : <p className="text-sm text-emerald-800 mt-2">Il codice ti sarà comunicato via email dall'organizzatore.</p>}
                {play.digital_instructions && <p className="text-xs text-slate-600 mt-2">{play.digital_instructions}</p>}
              </div>
            ) : (
            <div className="flex justify-center mb-4">
              <QRCodeCanvas value={play.receipt_serial} size={120} includeMargin />
            </div>
            )}
            <div className="text-sm space-y-1 border-t border-dashed border-slate-300 pt-3">
              <Row label="Concorso" value={contest.name} />
              <Row label="Seriale" value={play.receipt_serial} />
              <Row label="Data" value={new Date(play.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })} />
              {play.expires_at && !play.digital && (
                <Row label="Valida fino al" value={new Date(play.expires_at).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" })} />
              )}
            </div>
            <p className="text-[11px] text-center text-slate-500 mt-4">
              {play.digital ? "Il premio è stato consegnato in forma digitale: conserva questa email/ricevuta come riferimento." : "Presenta questa ricevuta (o il QR) al punto vendita per ritirare il premio."}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 sm:px-8 pb-8 space-y-3">
          {contest.marketing_consent_text && (
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="consent-label">
              <input type="checkbox" checked={consent} onChange={(e) => toggleConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" data-testid="consent-checkbox" />
              <span>{contest.marketing_consent_text}</span>
            </label>
          )}
          {web ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="web-receipt-info">
              <p className="font-semibold">Ricevuta inviata via email a {play.player_email || "il tuo indirizzo"}.</p>
              <p className="text-xs mt-1">{play.digital ? "Premio digitale: il codice è nell'email e qui sopra, nessun ritiro in negozio." : "Per ritirare il premio mostra in negozio il QR della ricevuta (anche da \"I miei premi\")."}</p>
            </div>
          ) : (
          <div className="flex gap-2">
            <Input
              data-testid="receipt-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@esempio.it"
              className="bg-slate-100 border-slate-200 text-slate-900"
            />
            <Button
              data-testid="send-email-button"
              onClick={sendEmail}
              disabled={sending || sent}
              className="gap-2 shrink-0 bg-blue-600 hover:bg-blue-700"
            >
              {sent ? <Check className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
              {emailLabel(sent, sending)}
            </Button>
          </div>
          )}
          {waEnabled && !web && (
            <div className="flex gap-2">
              <Input
                data-testid="receipt-phone-input"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s]/g, ""))}
                placeholder="+39 333 1234567"
                className="bg-slate-100 border-slate-200 text-slate-900"
              />
              <Button
                data-testid="send-whatsapp-receipt-button"
                onClick={sendWhatsApp}
                disabled={waState !== "idle"}
                className="gap-2 shrink-0 bg-emerald-600 hover:bg-emerald-700"
              >
                {waState === "sent" ? <Check className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                {WA_LABEL[waState] || "WhatsApp"}
              </Button>
            </div>
          )}
          <div className={`grid gap-3 ${web ? "grid-cols-1" : "grid-cols-2"}`}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="send-whatsapp-button">
              <Button variant="outline" className="w-full gap-2 border-emerald-300 text-emerald-700 h-12">
                <MessageCircle className="h-4 w-4" /> Condividi
              </Button>
            </a>
            {!web && <Button
              data-testid="print-receipt-button"
              variant="outline"
              onClick={print}
              className="w-full gap-2 border-slate-300 text-slate-700 h-12"
            >
              <Printer className="h-4 w-4" /> Stampa Totem
            </Button>}
          </div>
          {!web && mode === "browser" && autoPrintState && (
            <p className={`text-xs text-center rounded-md px-3 py-1.5 ${autoPrintState === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="auto-print-state" data-state={autoPrintState}>
              {autoPrintState === "sent" ? "Stampa automatica inviata alla stampante predefinita del totem" : "Stampa automatica disattivata per questo concorso: usa \"Stampa Totem\""}
            </p>
          )}
          {!web && mode !== "browser" && (
            <>
            <PrintJobStatus playId={play.id} active={queued} onDone={() => playSound?.("printed")} />
            <Button
              data-testid="thermal-print-button"
              onClick={() => thermalPrint({ manual: true })}
              disabled={printState === "printing" || (mode === "usb" && !usbSupported())}
              className={`w-full gap-2 h-12 ${printState === "done" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-800 hover:bg-slate-900"}`}
            >
              {mode === "usb" ? <Usb className="h-4 w-4" /> : <Printer className="h-4 w-4" />}
              {thermalLabel(printState, mode)}
            </Button>
            </>
          )}
          <a href={api.receiptPdfUrl(play.id, play.receipt_token)} target="_blank" rel="noopener noreferrer" data-testid="download-pdf-button">
            <Button variant="outline" className="w-full gap-2 border-slate-300 text-slate-700 h-12">
              <FileDown className="h-4 w-4" /> Scarica PDF
            </Button>
          </a>
          {web && <a href="/i-miei-premi" data-testid="web-my-prizes-link"><Button className="w-full gap-2 h-12 bg-blue-600 hover:bg-blue-700"><Check className="h-4 w-4" /> Vai a "I miei premi"</Button></a>}
          <Button data-testid="win-new-play-button" onClick={onClose} variant="ghost" className="w-full">
            Nuova giocata
          </Button>
        </div>
      </motion.div>
    </>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-slate-500">{label}:</span>
    <span className="font-semibold text-right">{value}</span>
  </div>
);
